// src/components/common/InteractiveTableCard.jsx
import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, X, Copy, Check } from 'lucide-react';

const InteractiveTableCard = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const tableContainerRef = useRef(null);

  const handleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(true);
  };
  
  const handleCollapse = () => setIsExpanded(false);

  const handleCopy = useCallback(() => {
    if (!tableContainerRef.current) return;
    const table = tableContainerRef.current.querySelector('table');
    if (!table) return;

    const rows = Array.from(table.querySelectorAll('tr'));
    const tsv = rows.map(row => {
      const cells = Array.from(row.querySelectorAll('th, td'));
      return cells.map(cell => {
        // Basic TSV sanitation: remove tabs and newlines from content
        const text = (cell.textContent || '').replace(/\s+/g, ' ');
        return text;
      }).join('\t');
    }).join('\n');

    navigator.clipboard.writeText(tsv).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2500);
    }).catch(err => {
      console.error("Failed to copy table to clipboard:", err);
    });
  }, []);


  const tableMarkup = (
    <div className="overflow-x-auto show-scrollbar-thin">
      <table className="min-w-full text-sm">
        {children}
      </table>
    </div>
  );

  return (
    <>
      {/* The container in the chat bubble. */}
      <div 
        className="relative group my-2 transition-transform duration-200 ease-out hover:scale-[1.02] hover:z-10 cursor-pointer"
        onClick={handleExpand}
      >
        <div className="relative bg-surface-dark/70 rounded-lg border border-border-subtle overflow-hidden shadow-lg">
          {tableMarkup}
        </div>
        <div
          className="absolute top-2 right-2 flex items-center justify-center w-7 h-7 bg-accent/90 text-white rounded-full shadow-lg backdrop-blur-sm transition-all opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100"
          aria-label="Expand table"
        >
          <Maximize2 size={16} />
        </div>
      </div>

      {/* The modal portal with improved styling and copy functionality */}
      {isExpanded && createPortal(
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md animate-fade-in-transform"
          onClick={handleCollapse}
          role="dialog"
          aria-modal="true"
        >
          <div 
            className="relative w-[90vw] max-w-6xl max-h-[85vh] bg-surface rounded-xl shadow-2xl flex flex-col border border-border-subtle/80 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border-subtle)', backgroundColor: 'var(--surface-dark)' }}>
              <h3 className="text-lg font-semibold text-on-surface">Table Viewer</h3>
              <div className="flex items-center gap-2">
                <button 
                  onClick={handleCopy}
                  className="p-1.5 rounded-full text-on-surface-dim hover:text-on-surface hover:bg-white/10 transition-colors"
                  aria-label={isCopied ? "Copied!" : "Copy as TSV"}
                  disabled={isCopied}
                >
                  {isCopied ? <Check size={22} className="text-success" /> : <Copy size={22} />}
                </button>
                <button 
                  onClick={handleCollapse}
                  className="p-1.5 rounded-full text-on-surface-dim hover:text-on-surface hover:bg-white/10 transition-colors"
                  aria-label="Close table viewer"
                >
                  <X size={22} />
                </button>
              </div>
            </div>
            <div ref={tableContainerRef} className="overflow-auto p-4 show-scrollbar-thin">
              <table className="min-w-full text-sm">{children}</table>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default InteractiveTableCard;